import React, { useCallback, useMemo, useState, useEffect } from 'react'
import classnames from 'classnames'
import bluebird from 'bluebird'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'

import Table from '@nocode-works/template/components/table/SimpleTable'
import Window from '@nocode-works/template/components/dialog/Window'
import WebsiteScreenshot from 'components/widgets/WebsiteScreenshot'
import QuotaSummary from 'components/widgets/QuotaSummary'
import QuotaWarning from 'components/widgets/QuotaWarning'
import CreateDialog from 'components/website/CreateDialog'

import appsumoSelectors from 'store/selectors/appsumo'
import userActions from 'store/modules/user'
import userSelectors from 'store/selectors/user'
import routerActions from '@nocode-works/template/store/modules/router'
import publishActions from '@nocode-works/template/store/modules/publish'
import routerSelectors from '@nocode-works/template/store/selectors/router'
import websiteSelectors from '@nocode-works/template/store/selectors/website'
import websiteActions from '@nocode-works/template/store/modules/website'
import icons from '@nocode-works/template/icons'

const AddIcon = icons.add
const EditIcon = icons.code
const PublishIcon = icons.send
const DeleteIcon = icons.delete
const SettingsIcon = icons.settings

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    flexGrow: 0,
  },
  titleFiller: {
    flexGrow: 1,
    textAlign: 'center',
  },
  addButton: {
    flexGrow: 0,
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  description: {
    color: '#999'
  },
  deletePadding: {
    height: theme.spacing(2),
  },
  button: {
    textTransform: 'none',
  },
  greyButton: {
    color: '#999',
  },
  smallText: {
    fontSize: '0.8em',
  },
  smallGreyText: {
    fontSize: '0.8em',
    color: '#666',
  },
  smallRedText: {
    fontSize: '0.8em',
    color: '#900',
  }
}))

const FIELDS =[{
  title: 'Screenshot',
  name: 'screenshot',
}, {
  title: 'Name',
  name: 'name',
}, {
  title: 'Created',
  name: 'created',
}, {
  title: 'Product',
  name: 'product',
}, {
  title: 'Status',
  name: 'status',
}, {
  title: 'Quota Used',
  name: 'quota',
}, {
  title: 'URL',
  name: 'url',
}]

const WebsiteList = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [ creating, setCreating ] = useState(false)
  const [ deletingItem, setDeletingItem ] = useState(null)
  const [ deletingConfirmName, setDeletingConfirmName ] = useState('')
  const data = useSelector(websiteSelectors.websiteList)
  const appsumoPlan = useSelector(appsumoSelectors.activePlan)
  const params = useSelector(routerSelectors.params)
  let showNewWebsiteDialog = useSelector(userSelectors.showNewWebsiteDialog)
  if(params.shownew) showNewWebsiteDialog = true

  const addButtonDisabled = useMemo(() => {
    if(!appsumoPlan) return false
    return data.length >= appsumoPlan.websites
  }, [
    appsumoPlan,
    data,
  ])

  const onAdd = useCallback(() => {
    setCreating(true)
  })

  const onEdit = useCallback((item) => {
    dispatch(routerActions.navigateTo('website.edit', {id: item.id}))
  })

  const onOpenBuilder = useCallback((item) => {
    dispatch(websiteActions.openBuilder(item.id))
  })

  const onCancelDelete = useCallback(() => {
    setDeletingItem(null)
  })

  const onConfirmDelete = useCallback(async () => {
    const result = await dispatch(websiteActions.delete(deletingItem.id))
    if(result) {
      setDeletingItem(null)
    }
  }, [
    deletingItem,
  ])

  const onCancelCreateDialog = useCallback(async () => {
    await dispatch(userActions.updateMeta({
      data: {
        showNewWebsiteDialog: false,
      }
    }))
    setCreating(false)
  }, [])

  const onSubmitCreateDialog = useCallback(async ({
    name,
    template,
    options,
    layout,
  }) => {
    dispatch(userActions.updateMeta({
      data: {
        showNewWebsiteDialog: false,
      }
    }))

    await dispatch(websiteActions.create({
      name,
      template,
      options,
      layout,
    }))
  }, [])

  const tableData = useMemo(() => {

    const sortedData = data.sort((a, b) => {
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()
      if (aName > bName) return 1
      else if(aName < bName) return -1
      else return 0
    })

    return sortedData.map(website => {
      return {
        id: website.id,
        website,
        name: (
          <a href="#" onClick={ () => onOpenBuilder(website) }>
            { website.name }
          </a>
        ),
        created: (
          <div className={ classes.smallText }>
            { new Date(website.created_at).toLocaleString() }
          </div>
        ),
        product: 
          (
            website.product ?
              website.product.metadata.nocodeId :
              'free'
          ),
        status: website.collaboration_type,
        quota: (
          <QuotaSummary
            website={ website }
          />
        ),
        screenshot: (
          <a href="#" onClick={ () => onOpenBuilder(website) }>
            <WebsiteScreenshot
              image={ website.screenshot }
            />
          </a>
        ),
        url: website.url ? (
          <a href={ website.url } target="_blank" className={ classes.smallText }>
            { website.url }
          </a>
        ) : (
          <div>
            <div className={ classes.smallGreyText }>
              { website.summary_url }
            </div>
            <div className={ classes.smallRedText }>
              (* not published)
            </div>
          </div>
          
        ),
      }
    })
  }, [
    data,
    classes,
  ])

  const getActions = useCallback((item) => {
    return (
      <>
        {
          item.status == 'owner' && (
            <Button
              className={ classnames(classes.actionButton, classes.button, classes.greyButton) }
              size="small"
              variant="outlined"
              onClick={ () => setDeletingItem(item.website) }
            >
              <DeleteIcon />&nbsp;&nbsp;Delete
            </Button>
          )
        }
        <Button
          className={ classnames(classes.actionButton, classes.button, classes.greyButton) }
          size="small"
          variant="outlined"
          onClick={ (e) => {
            onEdit(item)
            return false
          }}
        >
          <SettingsIcon />&nbsp;&nbsp;Settings
        </Button>
        <Button
          className={ classnames(classes.actionButton, classes.button) }
          size="small"
          color="secondary"
          variant="outlined"
          onClick={ async (e) => {
            onEdit(item)
            await bluebird.delay(200)
            await dispatch(publishActions.publishCore())
          }}
        >
          <PublishIcon />&nbsp;&nbsp;Publish
        </Button>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          className={ classnames(classes.actionButton, classes.button) }
          onClick={ () => onOpenBuilder(item) }
        >
          <EditIcon />&nbsp;&nbsp;Builder
        </Button>
      </>
    )
  })

  useEffect(() => {
    if(showNewWebsiteDialog) {
      onAdd()
    }
  }, [
    showNewWebsiteDialog,
  ])

  return (
    <div className={ classes.root }>
      <div className={ classes.toolbar }>
        <div className={ classes.title }>
          <Typography gutterBottom variant="h6">
            Websites
          </Typography>
        </div>
        <div className={ classes.titleFiller }>
          <QuotaWarning />
        </div>
        <div className={ classes.addButton }>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={ addButtonDisabled }
            onClick={ onAdd }
          >
            <AddIcon />&nbsp;&nbsp;Add Website
          </Button>
        </div>
      </div>
      {
        appsumoPlan && (
          <div className={ classes.toolbar }>
           You are using { data.length } of your { appsumoPlan.websites } websites in your AppSumo plan.
          </div>
        )
      }
      <div className={ classes.content }>
        <Table
          data={ tableData }
          fields={ FIELDS }
          getActions={ getActions }
        />
      </div>
      {
        deletingItem && (
          <Window
            open
            size="sm"
            withCancel
            title="Confirm delete"
            submitTitle="Delete"
            disabled={ deletingConfirmName.toLowerCase() == deletingItem.name.toLowerCase() ? false : true }
            onCancel={ onCancelDelete }
            onSubmit={ onConfirmDelete }
          >
            <Typography gutterBottom>
              Are you sure you want to delete { deletingItem.name }?
            </Typography>
            <TextField
              label={ `Type "${ deletingItem.name }" to confirm deletion`}
              fullWidth
              value={ deletingConfirmName }
              onChange={ (e) => setDeletingConfirmName(e.target.value) }
            />
            <div className={ classes.deletePadding }></div>
          </Window>
        )
      }
      {
        creating && (
          <CreateDialog
            onCancel={ onCancelCreateDialog }
            onSubmit={ onSubmitCreateDialog }
          />
        )
      }
    </div>
  )
}

export default WebsiteList